import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    GetNotesTypesNamesAndIdsDTO,
    GetTranslationsDto,
    INotesOfAnnualReportDTO,
    INotesTemplateDTO,
    INotesTemplateEntityDTO,
    INotesTypeEntityDTO,
    UpdateNotesTypeDto,
    UpdateTranslationsDto,
} from '@modeso/types__aumico-lib-notes-be';
import { Store, select } from '@ngrx/store';
import Debug from 'debug';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as fromNotesTemplatesActions from '../actions/notesTemplates.actions';
import * as fromNotesTypesActions from '../actions/notesTypes.actions';
import * as fromTranslationsActions from '../actions/translations.actions';
import { ILegalFormAndAccountBasisCombinationCheck } from '../interfaces/ILegalFormAndAccountBasisCombinationCheck.interface';
import { INameExistenceFiltration } from '../interfaces/INameExistenceFiltration.interface';
import { INotesTemplateByLegalFormAndAccountBasisRequest } from '../interfaces/requests/INotesTemplateByLegalFormAndAccountBasisRequest.interface';
import * as fromFeature from '../reducers/notes.reducer';
import { AppState, translationsStateSelectors } from '../reducers/notes.reducer';
import { INotesProvider } from './notes.provider.interface';

const debug = Debug('aumico:aumico-lib-notes-fe:NotesProvider');

@Injectable({
    providedIn: 'root',
})
export class NotesProvider implements INotesProvider {
    constructor(private store: Store<AppState>) {}

    // notes template

    dispatchNotesTemplates(): void {
        this.store.dispatch(fromNotesTemplatesActions.getNotesTemplates());
    }

    getNotesTemplates$(): Observable<INotesTemplateDTO[]> {
        return this.store.select(fromFeature.selectAllNotesTemplates);
    }

    selectActiveNotesTemplate(id: string): void {
        this.store.dispatch(fromNotesTemplatesActions.onSelectNotesTemplate({ payload: id }));
    }

    getActiveNotesTemplate$(): Observable<INotesTemplateDTO> {
        return this.store.select(fromFeature.selectActiveNotesTemplate);
    }

    createNotesTemplate(notesTemplate: INotesTemplateEntityDTO): void {
        this.store.dispatch(fromNotesTemplatesActions.createNotesTemplate({ payload: notesTemplate }));
    }

    editNotesTemplate(notesTemplate: INotesTemplateEntityDTO): void {
        this.store.dispatch(fromNotesTemplatesActions.updateNotesTemplate({ payload: notesTemplate }));
    }

    checkNotesTemplateNameExistence$(filtration: INameExistenceFiltration): Observable<boolean> {
        return this.store.select(fromFeature.selectNotesTemplateNameExistence, filtration);
    }

    checkLegalFormAndAccountBasisCombination$(
        combination: ILegalFormAndAccountBasisCombinationCheck,
    ): Observable<boolean> {
        return this.store.select(fromFeature.selectLegalFormAndAccountBasisExistence, combination);
    }

    getNotesTemplateError$(): Observable<HttpErrorResponse> {
        return this.store.select(fromFeature.selectNotesTemplateError);
    }

    dispatchNotesOfAnnualReport(combination: INotesTemplateByLegalFormAndAccountBasisRequest) {
        this.store.dispatch(fromNotesTemplatesActions.getNotes({ payload: combination }));
    }

    getNotesOfAnnualReport$(): Observable<INotesOfAnnualReportDTO> {
        return this.store
            .select(fromFeature.selectNotesTemplateByLegalFormAndAccountBasis)
            .pipe(filter((notes: INotesOfAnnualReportDTO) => notes != null));
    }

    dispatchNotesTemplateById(id: string) {
        this.store.dispatch(fromNotesTemplatesActions.getNotesTemplateById.do({ payload: id }));
    }

    resetNotes() {
        this.store.dispatch(fromNotesTemplatesActions.resetNotes());
    }

    // notes type

    dispatchNotesTypes(): void {
        this.store.dispatch(fromNotesTypesActions.getNotesTypes());
    }

    getNotesTypes$(): Observable<INotesTypeEntityDTO[]> {
        return this.store.select(fromFeature.selectAllNotesTypes);
    }

    getNotesTypesNamesAndIds$(): Observable<GetNotesTypesNamesAndIdsDTO[]> {
        return this.store.select(fromFeature.selectAllNotesTypesNamesAndIds);
    }

    public dispatchGetAllNotesTypesNamesAndIds(): void {
        return this.store.dispatch(fromNotesTypesActions.getAllNotesTypesNamesAndIds.getAllNotesTypesNamesAndIds());
    }

    selectActiveNotesType(id: string): void {
        this.store.dispatch(fromNotesTypesActions.onSelectNotesType({ payload: id }));
    }

    getActiveNotesType$(): Observable<INotesTypeEntityDTO> {
        return this.store.select(fromFeature.selectActiveNotesType);
    }

    createNotesType(notesType: INotesTypeEntityDTO): void {
        this.store.dispatch(fromNotesTypesActions.createNotesType({ payload: notesType }));
    }

    editNotesType(notesType: UpdateNotesTypeDto): void {
        this.store.dispatch(fromNotesTypesActions.updateNotesType({ payload: notesType }));
    }

    checkNotesTypeNameExistence$(filtration: INameExistenceFiltration): Observable<boolean> {
        return this.store.select(fromFeature.selectNotesTypeNameExistence, filtration);
    }

    getNotesTypeError$(): Observable<HttpErrorResponse> {
        return this.store.select(fromFeature.selectNotesTypeError);
    }

    getTranslations$(): Observable<GetTranslationsDto> {
        return this.store.pipe(select(translationsStateSelectors.selectTranslations));
    }

    dispatchGetTranslations(locales: string[], untranslatedOnly: boolean): void {
        this.store.dispatch(fromTranslationsActions.getTranslations({ locales, untranslatedOnly }));
    }

    dispatchUpdateTranslations(translations: UpdateTranslationsDto): void {
        this.store.dispatch(fromTranslationsActions.updateTranslations({ payload: translations }));
    }
}
